import { useState, useEffect } from 'react';
import fetchAPI from 'app/helpers/fetch';
import { State, PENDING, REJECTED, FULFILLED } from 'admin-mojo/helpers/StateNotification';
import { Filters } from 'app/CasinoFilters/types';
import { CasinoGamesProps, PaginatedGames, GameSections } from './types';

const SEARCH_URL = '/casino/search';

const emptyGames: PaginatedGames = {
  data: [],
  meta: { page: 1, per: 1, total_pages: 0, total_count: 0 },
};

const compactFilters = (filters: Filters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.append(key, value);
  });

  return params;
};

type UseGames = (props: CasinoGamesProps) => {
  state: State;
  gameSections: GameSections;
  searchParams: string;
};

// eslint-disable-next-line max-lines-per-function
const useGames: UseGames = (props) => {
  const [state, setState] = useState<State>({ status: PENDING });
  const [gameSections, setGameSections] = useState<GameSections>(props.gameSections);
  const [initialLocation] = useState(window.location.pathname);
  const [searchParams, setSearchParams] = useState(window.location.search?.replace(/^\?/, '') || '');
  const [initialLoadSkipped, setInitialLoadSkipped] = useState(false);

  useEffect(() => {
    const makeSearchRequest = (e: Event) => {
      const newSearchParams = compactFilters((e as CustomEvent<Filters>).detail).toString();
      setSearchParams(newSearchParams);
    };
    document.addEventListener('casinoSearch', makeSearchRequest);
    return () => {
      document.removeEventListener('casinoSearch', makeSearchRequest);
    };
  }, []);

  const load = (url: string) => {
    setState({ status: PENDING });

    fetchAPI(url, { method: 'GET', body: null })
      .then((data: PaginatedGames) => {
        setGameSections({ results: data });
        setState({ status: FULFILLED });

        const hideSliderEvent = new CustomEvent('hideSlider');
        document.dispatchEvent(hideSliderEvent);
        const content = document.querySelector('.content');
        if (content) {
          content.scrollIntoView();
        }
      })
      .catch((v) => {
        setState({ status: REJECTED, error: v });
      });
  };

  useEffect(() => {
    if (!initialLoadSkipped) {
      // Initial game groups are taken from props. The props are built using search params. No need to load games again.
      setInitialLoadSkipped(true);
      return;
    }

    let location;

    if (searchParams) {
      // Changing the searchParams reloads the game list. Pagination is handled in SectionGrid.
      location = `${SEARCH_URL}?${searchParams}`;
      load(`${SEARCH_URL}.json?${searchParams}`);
    } else if (initialLocation === SEARCH_URL) {
      // we don't have any search params, but we started on a search page so we can't back to
      // default view - we don't have default favs/games for no-search state. Just clear all results
      // and let user re-search again
      location = SEARCH_URL;
      setGameSections({ results: emptyGames });
    } else {
      // reset to initial state
      location = initialLocation;
      setGameSections(props.gameSections);
    }
    window.history.pushState(null, '', location);
  }, [searchParams]);

  return { state, gameSections, searchParams };
};

export default useGames;
